import {
    collection,
    doc,
    getDoc,
    getDocs,
    setDoc,
    updateDoc,
    deleteDoc,
    query,
    where,
    orderBy,
    serverTimestamp,
    onSnapshot,
    type Unsubscribe
} from 'firebase/firestore';
import { db, auth } from '@/lib/firebase';
import type { Playlist, CreatePlaylistData, UpdatePlaylistData } from '@/types';
import { convertTimestamps, TIMESTAMP_FIELDS } from '@/utils/firestore';

export class PlaylistService {
    private static readonly COLLECTION = 'playlists';

    static async createPlaylist(data: CreatePlaylistData): Promise<Playlist> {
        const user = auth.currentUser;
        if (!user) throw new Error('User must be authenticated to create a playlist');

        // Generate a new document reference with an auto-generated ID
        const newPlaylistRef = doc(collection(db, this.COLLECTION));

        // Construct the playlist object
        const playlistData = {
            ...data,
            id: newPlaylistRef.id, // Use the generated ID
            ownerId: user.uid,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
            collaborators: {
                [user.uid]: true // Owner is a collaborator by default
            },
            settings: data.settings,
            metadata: {
                totalVideos: 0
            }
        };

        // Save to Firestore
        await setDoc(newPlaylistRef, playlistData);

        // Return the object with Dates (approximation)
        return {
            ...playlistData,
            createdAt: new Date(),
            updatedAt: new Date(),
        } as Playlist;
    }

    static async getPlaylist(playlistId: string): Promise<Playlist | null> {
        const docRef = doc(db, this.COLLECTION, playlistId);
        const snap = await getDoc(docRef);

        if (snap.exists()) {
            return convertTimestamps(snap.data() as Playlist, TIMESTAMP_FIELDS.playlist as any);
        }
        return null;
    }

    static async getUserPlaylists(userId: string): Promise<Playlist[]> {
        // Query playlists where the user is listed in the collaborators map
        // Note: We need an index for this query: collections('playlists').where('collaborators.{userId}').equals(true)
        // Alternatively, simpler MVP approach: where('ownerId', '==', userId) if we only care about owned ones first.
        // The spec says collaborators should be able to see it too.
        // However, Firestore map queries can be tricky without specific indexes or structures.
        // A common pattern is an array `collaboratorIds` for "array-contains" queries.
        // Our type definition in `index.ts` says `collaborators: Record<string, boolean>`.
        // Valid query: where(`collaborators.${userId}`, '==', true)

        const q = query(
            collection(db, this.COLLECTION),
            where(`collaborators.${userId}`, '==', true),
            orderBy('updatedAt', 'desc')
        );

        const querySnapshot = await getDocs(q);

        return querySnapshot.docs.map(doc =>
            convertTimestamps(doc.data() as Playlist, TIMESTAMP_FIELDS.playlist as any)
        );
    }

    static async updatePlaylist(playlistId: string, updates: UpdatePlaylistData): Promise<void> {
        const docRef = doc(db, this.COLLECTION, playlistId);

        await updateDoc(docRef, {
            ...updates,
            updatedAt: serverTimestamp(),
        });
    }

    static async addCollaborator(playlistId: string, userId: string): Promise<void> {
        const docRef = doc(db, this.COLLECTION, playlistId);
        await updateDoc(docRef, {
            [`collaborators.${userId}`]: true,
            updatedAt: serverTimestamp()
        });
    }

    static async removeCollaborator(playlistId: string, userId: string): Promise<void> {
        const docRef = doc(db, this.COLLECTION, playlistId);
        const { deleteField } = await import('firebase/firestore');
        await updateDoc(docRef, {
            [`collaborators.${userId}`]: deleteField(),
            updatedAt: serverTimestamp()
        });
    }

    static async deletePlaylist(playlistId: string): Promise<void> {
        const docRef = doc(db, this.COLLECTION, playlistId);
        await deleteDoc(docRef);
    }

    static subscribeToPlaylist(playlistId: string, callback: (playlist: Playlist | null) => void): Unsubscribe {
        const docRef = doc(db, this.COLLECTION, playlistId);

        return onSnapshot(docRef, (snap) => {
            if (snap.exists()) {
                const playlist = convertTimestamps(snap.data() as Playlist, TIMESTAMP_FIELDS.playlist as any);
                callback(playlist);
            } else {
                callback(null);
            }
        });
    }
}
